import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { standaloneAlterationsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Scissors, User, Clock, IndianRupee, Zap, HardHat,
  CheckCircle2, Circle, CreditCard, Loader2, ChevronRight, Phone,
} from 'lucide-react';

const STATUS_FLOW = ['received', 'assessed', 'in_progress', 'completed', 'delivered'];
const STATUS_LABELS = {
  received: 'Received', assessed: 'Assessed', in_progress: 'In Progress',
  completed: 'Completed', delivered: 'Delivered', cancelled: 'Cancelled',
};
const STATUS_COLORS = {
  received: { bg: 'rgba(37,99,235,0.08)', text: '#2563EB' },
  assessed: { bg: 'rgba(147,51,234,0.08)', text: '#9333EA' },
  in_progress: { bg: 'rgba(245,158,11,0.10)', text: '#D97706' },
  completed: { bg: 'rgba(16,185,129,0.08)', text: '#059669' },
  delivered: { bg: 'rgba(27,42,74,0.08)', text: '#1B2A4A' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626' },
};
const PAYMENT_COLORS = {
  unpaid: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626' },
  partial: { bg: 'rgba(245,158,11,0.10)', text: '#D97706' },
  paid: { bg: 'rgba(16,185,129,0.08)', text: '#059669' },
};
const GARMENT_LABELS = {
  jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta', pyjama: 'Pyjama',
  waistcoat: 'Waistcoat', dress: 'Dress', other: 'Other',
};
const ALT_TYPE_LABELS = {
  shorten: 'Shorten', lengthen: 'Lengthen', take_in: 'Take In', let_out: 'Let Out',
  reshape: 'Reshape', reposition: 'Reposition', reline: 'Reline', patch: 'Patch',
  zipper: 'Zipper', button_fix: 'Button Fix', other: 'Other',
};
const AREA_LABELS = {
  chest: 'Chest', waist: 'Waist', hips: 'Hips', shoulders: 'Shoulders', sleeves: 'Sleeves',
  jacket_length: 'Jacket Length', trouser_length: 'Trouser Length', trouser_waist: 'Trouser Waist',
  collar: 'Collar', cuffs: 'Cuffs', lapels: 'Lapels', lining: 'Lining',
  seat: 'Seat', inseam: 'Inseam', outseam: 'Outseam', other: 'Other',
};

export default function AlterationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alt, setAlt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('cash');
  const [paying, setPaying] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [assignWorkerId, setAssignWorkerId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const fetchData = async () => {
    try {
      const res = await standaloneAlterationsAPI.get(id);
      setAlt(res.data);
    } catch { toast.error('Failed to load'); navigate('/app/alterations'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await standaloneAlterationsAPI.updateStatus(id, { status: newStatus });
      setAlt(res.data);
      toast.success(`Status updated to ${STATUS_LABELS[newStatus]}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handlePayment = async () => {
    setPaying(true);
    try {
      const res = await standaloneAlterationsAPI.recordPayment(id, {
        amount: parseFloat(payAmount), method: payMethod,
      });
      setAlt(res.data);
      setShowPayment(false);
      setPayAmount('');
      toast.success('Payment recorded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setPaying(false);
  };

  const handleAssign = async () => {
    setAssigning(true);
    try {
      const res = await standaloneAlterationsAPI.updateStatus(id, {
        status: 'in_progress',
        assigned_worker_id: assignWorkerId,
      });
      setAlt(res.data);
      setShowAssign(false);
      toast.success('Worker assigned, status set to In Progress');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setAssigning(false);
  };

  const openAssign = async () => {
    try {
      const { workersAPI } = await import('@/lib/api');
      const res = await workersAPI.list();
      setWorkers((res.data?.workers || res.data || []).filter(w => w.is_active));
    } catch { }
    setShowAssign(true);
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  if (!alt) return null;

  const sc = STATUS_COLORS[alt.status] || STATUS_COLORS.received;
  const pc = PAYMENT_COLORS[alt.payment_status] || PAYMENT_COLORS.unpaid;
  const currentIdx = STATUS_FLOW.indexOf(alt.status);
  const nextStatus = currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[currentIdx + 1] : null;
  const balance = (alt.customer_charge || 0) - (alt.amount_paid || 0);

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="alteration-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-lg" onClick={() => navigate('/app/alterations')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold" style={{ color: 'var(--brand-navy)' }} data-testid="alt-number">
              {alt.alteration_number}
            </h1>
            <Badge variant="outline" className="text-[10px] px-2 py-0.5 rounded-md font-semibold"
              style={{ backgroundColor: sc.bg, color: sc.text }} data-testid="alt-status-badge">
              {STATUS_LABELS[alt.status]}
            </Badge>
            {alt.is_express && (
              <Badge className="text-[10px] px-2 py-0.5 font-bold gap-1 rounded-md"
                style={{ backgroundColor: 'rgba(217,119,6,0.10)', color: '#D97706', border: '1px solid rgba(217,119,6,0.3)' }}
                data-testid="alt-express-badge">
                <Zap className="w-3 h-3" /> EXPRESS
              </Badge>
            )}
          </div>
          <p className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>
            Created {new Date(alt.created_at).toLocaleDateString()} by {alt.created_by_name}
          </p>
        </div>
        <div className="flex gap-2">
          {nextStatus === 'in_progress' ? (
            <Button size="sm" className="gap-1 text-xs rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={openAssign} data-testid="assign-worker-btn">
              <HardHat className="w-3 h-3" /> Assign to Karigar
            </Button>
          ) : nextStatus ? (
            <Button size="sm" className="gap-1 text-xs rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={() => handleStatusChange(nextStatus)} data-testid="advance-status-btn">
              <ChevronRight className="w-3 h-3" /> {STATUS_LABELS[nextStatus]}
            </Button>
          ) : null}
          <Button size="sm" variant="outline" className="gap-1 text-xs rounded-lg"
            onClick={() => setShowPayment(true)} data-testid="record-payment-btn">
            <CreditCard className="w-3 h-3" /> Record Payment
          </Button>
        </div>
      </div>

      {/* Status Progress */}
      <div className="flex items-center gap-1" data-testid="status-progress">
        {STATUS_FLOW.map((s, i) => {
          const done = i <= currentIdx;
          const c = done ? STATUS_COLORS[s]?.text || '#059669' : 'var(--ink-muted)';
          return (
            <div key={s} className="flex items-center gap-1 flex-1">
              <div className="flex items-center gap-1.5 flex-1">
                {done ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: c }} /> : <Circle className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(27,42,74,0.2)' }} />}
                <span className="text-[10px] font-semibold whitespace-nowrap" style={{ color: done ? c : 'var(--ink-muted)' }}>{STATUS_LABELS[s]}</span>
              </div>
              {i < STATUS_FLOW.length - 1 && <div className="flex-1 h-px mx-1" style={{ backgroundColor: done ? c : 'rgba(27,42,74,0.1)' }} />}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Client + Garment */}
        <div className="lg:col-span-2 space-y-4">
          {/* Client Info */}
          <Card className="rounded-xl border bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5" style={{ color: 'var(--brand-navy)' }}>
                <User className="w-3.5 h-3.5" /> Client
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ backgroundColor: 'rgba(124,58,237,0.08)', color: '#7C3AED' }}>
                  {(alt.client_name || alt.walk_in_name || 'W')[0]}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>
                    {alt.client_name || alt.walk_in_name || 'Walk-in'}
                    {alt.client_id && (
                      <Link to={`/app/clients/${alt.client_id}`} className="text-[10px] ml-2 hover:underline" style={{ color: '#2563EB' }}>
                        View Profile
                      </Link>
                    )}
                  </p>
                  {alt.walk_in_phone && (
                    <p className="text-xs flex items-center gap-1" style={{ color: 'var(--ink-muted)' }}>
                      <Phone className="w-3 h-3" /> {alt.walk_in_phone}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Garment Info */}
          <Card className="rounded-xl border bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5" style={{ color: 'var(--brand-navy)' }}>
                <Scissors className="w-3.5 h-3.5" /> Garment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-[10px] uppercase" style={{ color: 'var(--ink-muted)' }}>Type</span>
                  <p className="font-medium" style={{ color: 'var(--brand-navy)' }}>{GARMENT_LABELS[alt.garment_type] || alt.garment_type}</p>
                </div>
                {alt.garment_brand && (
                  <div>
                    <span className="text-[10px] uppercase" style={{ color: 'var(--ink-muted)' }}>Brand</span>
                    <p className="font-medium" style={{ color: 'var(--brand-navy)' }}>{alt.garment_brand}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <span className="text-[10px] uppercase" style={{ color: 'var(--ink-muted)' }}>Description</span>
                  <p style={{ color: 'var(--navy-ink)' }}>{alt.garment_description || '—'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="rounded-xl border bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm" style={{ color: 'var(--brand-navy)' }}>Alteration Items ({alt.items?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(alt.items || []).map((item, idx) => (
                  <div key={item.id || idx} className="flex items-center justify-between p-2.5 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.08)' }}
                    data-testid={`detail-item-${idx}`}>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
                        {ALT_TYPE_LABELS[item.alteration_type] || item.alteration_type} — {AREA_LABELS[item.garment_area] || item.garment_area}
                      </p>
                      {item.description && <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{item.description}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums" style={{ color: 'var(--brand-navy)' }}>₹{item.customer_charge?.toLocaleString('en-IN')}</p>
                      <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{item.estimated_hours}h est.</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Activity Log */}
          <Card className="rounded-xl border bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm" style={{ color: 'var(--brand-navy)' }}>Activity Log</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(alt.activity_log || []).slice().reverse().map((log, idx) => (
                  <div key={log.id || idx} className="flex items-start gap-2 py-1.5">
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: 'rgba(124,58,237,0.4)' }} />
                    <div>
                      <p className="text-xs" style={{ color: 'var(--navy-ink)' }}>{log.notes}</p>
                      <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                        {log.by} &middot; {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Pricing + Worker */}
        <div className="space-y-4">
          {/* Pricing */}
          <Card className="rounded-xl border bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5" style={{ color: 'var(--brand-navy)' }}>
                <IndianRupee className="w-3.5 h-3.5" /> Pricing & Payment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--ink-muted)' }}>Customer Charge</span>
                <span className="font-semibold tabular-nums" style={{ color: 'var(--brand-navy)' }}>₹{(alt.customer_charge || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--ink-muted)' }}>Amount Paid</span>
                <span className="font-semibold tabular-nums" style={{ color: '#059669' }}>₹{(alt.amount_paid || 0).toLocaleString('en-IN')}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="font-semibold" style={{ color: 'var(--brand-navy)' }}>Balance</span>
                <span className="font-bold tabular-nums" style={{ color: balance > 0 ? '#DC2626' : '#059669' }}>
                  ₹{balance.toLocaleString('en-IN')}
                </span>
              </div>
              <Badge variant="outline" className="text-[10px] px-2 py-0.5 w-full justify-center"
                style={{ backgroundColor: pc.bg, color: pc.text }} data-testid="payment-status-badge">
                {alt.payment_status === 'paid' ? 'Fully Paid' : alt.payment_status === 'partial' ? 'Partially Paid' : 'Unpaid'}
              </Badge>
            </CardContent>
          </Card>

          {/* Worker */}
          <Card className="rounded-xl border bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5" style={{ color: 'var(--brand-navy)' }}>
                <HardHat className="w-3.5 h-3.5" /> Assigned Karigar
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alt.assigned_worker_name ? (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ backgroundColor: 'rgba(245,158,11,0.1)', color: '#D97706' }}>
                    {alt.assigned_worker_name[0]}
                  </div>
                  <span className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>{alt.assigned_worker_name}</span>
                </div>
              ) : (
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Not yet assigned</p>
              )}
            </CardContent>
          </Card>

          {/* Timing */}
          <Card className="rounded-xl border bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5" style={{ color: 'var(--brand-navy)' }}>
                <Clock className="w-3.5 h-3.5" /> Timing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span style={{ color: 'var(--ink-muted)' }}>Received</span>
                <span style={{ color: 'var(--navy-ink)' }}>{alt.received_date}</span>
              </div>
              {alt.required_by_date && (
                <div className="flex justify-between">
                  <span style={{ color: 'var(--ink-muted)' }}>Required By</span>
                  <span style={{ color: 'var(--navy-ink)' }}>{alt.required_by_date}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span style={{ color: 'var(--ink-muted)' }}>Est. Hours</span>
                <span style={{ color: 'var(--navy-ink)' }}>{alt.total_estimated_hours}h</span>
              </div>
            </CardContent>
          </Card>

          {/* Payments History */}
          {alt.payments && alt.payments.length > 0 && (
            <Card className="rounded-xl border bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm" style={{ color: 'var(--brand-navy)' }}>Payments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {alt.payments.map((p, i) => (
                  <div key={p.id || i} className="flex justify-between text-xs py-1.5 border-b last:border-0" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
                    <div>
                      <span className="font-medium" style={{ color: 'var(--brand-navy)' }}>₹{p.amount?.toLocaleString('en-IN')}</span>
                      <span className="ml-1 uppercase text-[10px]" style={{ color: 'var(--ink-muted)' }}>{p.method}</span>
                    </div>
                    <span style={{ color: 'var(--ink-muted)' }}>{new Date(p.recorded_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                placeholder={`Balance: ₹${balance.toLocaleString('en-IN')}`}
                className="h-9 text-sm mt-1" data-testid="payment-amount" />
            </div>
            <div>
              <Label className="text-xs">Method</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger className="h-9 text-sm mt-1" data-testid="payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayment(false)}>Cancel</Button>
            <Button style={{ backgroundColor: 'var(--brand-navy)' }} disabled={!payAmount || paying}
              onClick={handlePayment} data-testid="confirm-payment-btn">
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Record'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Worker Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>Assign to Karigar</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs">Select Worker</Label>
            <Select value={assignWorkerId} onValueChange={setAssignWorkerId}>
              <SelectTrigger className="h-9 text-sm mt-1" data-testid="worker-select">
                <SelectValue placeholder="Select karigar..." />
              </SelectTrigger>
              <SelectContent>
                {workers.map(w => (
                  <SelectItem key={w.id} value={w.id}>{w.name} ({w.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button style={{ backgroundColor: 'var(--brand-navy)' }} disabled={!assignWorkerId || assigning}
              onClick={handleAssign} data-testid="confirm-assign-btn">
              {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign & Start'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
